import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Box,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  useMediaQuery,
  useTheme,
  Avatar,
  Menu,
  MenuItem,
  Divider,
  Chip,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  Menu as MenuIcon,
  ChevronRight,
  Person,
  Logout,
} from '@mui/icons-material';
import { RootState, AppDispatch } from '../store';
import { logout, getCurrentUser } from '../store/slices/authSlice';
import Logo from './common/Logo';

const StyledAppBar = styled(AppBar)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  color: theme.palette.text.primary,
  borderBottom: `1px solid ${theme.palette.divider}`,
  boxShadow: 'none',
}));

interface NavItem {
  text: string;
  /** A route inside the app. */
  path?: string;
  /** A section of the landing page. */
  href?: string;
}

/**
 * Two audiences, two menus. A visitor sees what the product is; a signed-in user sees where their work is.
 * The five recipe steps are not here: they live in the workflow bar on the screens where you do them.
 */
const PUBLIC_ITEMS: NavItem[] = [
  { text: 'Product', href: '/#features' },
  { text: 'Pricing', href: '/#pricing' },
  { text: 'Guides', path: '/support' },
];

const APP_ITEMS: NavItem[] = [
  { text: 'Workspace', path: '/dashboard' },
  { text: 'Projects', path: '/projects' },
  { text: 'Reports', path: '/reports' },
  { text: 'History', path: '/history' },
  { text: 'Guides', path: '/support' },
];

export function isNavActive(pathname: string, item: NavItem): boolean {
  if (!item.path) return false;
  if (item.path === '/dashboard') return pathname === '/dashboard';
  if (item.path === '/history') return pathname.startsWith('/history') || pathname.startsWith('/analysis');
  return pathname.startsWith(item.path);
}

const Header: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { user, isAuthenticated } = useSelector((state: RootState) => state.auth);
  const { subscription } = useSelector((state: RootState) => state.subscription);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [userMenuAnchor, setUserMenuAnchor] = useState<null | HTMLElement>(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const location = useLocation();

  useEffect(() => {
    const refreshInterval = setInterval(() => {
      if (user && !user.emailVerified) {
        dispatch(getCurrentUser());
      }
    }, 30000);
    return () => clearInterval(refreshInterval);
  }, [dispatch, user?.emailVerified]);

  const handleDrawerToggle = () => setMobileOpen(!mobileOpen);
  const handleUserMenuOpen = (event: React.MouseEvent<HTMLElement>) =>
    setUserMenuAnchor(event.currentTarget);
  const handleUserMenuClose = () => setUserMenuAnchor(null);
  const handleLogout = () => {
    dispatch(logout());
    handleUserMenuClose();
  };

  const getInitials = () => {
    if (!user) return 'U';
    const name = user.name || '';
    const parts = name.split(' ').filter(Boolean);
    if (parts.length >= 2) {
      return `${parts[0].charAt(0)}${parts[1].charAt(0)}`.toUpperCase();
    }
    return (name.charAt(0) || 'U').toUpperCase();
  };

  const items: NavItem[] = [
    ...(isAuthenticated ? APP_ITEMS : PUBLIC_ITEMS),
    ...(isAuthenticated && user?.role === 'ADMIN' ? [{ text: 'Admin', path: '/admin' }] : []),
  ];

  const linkProps = (item: NavItem) => (item.path ? { component: Link as never, to: item.path } : { href: item.href });

  const drawer = (
    <>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 1 }}>
        <IconButton onClick={handleDrawerToggle} aria-label="close menu">
          <ChevronRight />
        </IconButton>
      </Box>
      <List dense>
        {items.map((item) => {
          const active = isNavActive(location.pathname, item);
          return (
            <ListItem key={item.text} disablePadding>
              <ListItemButton {...(item.path ? { component: Link, to: item.path } : { component: 'a', href: item.href })} onClick={handleDrawerToggle} selected={active}>
                <ListItemText
                  primary={item.text}
                  primaryTypographyProps={{ fontWeight: active ? 600 : 400, color: active ? 'primary' : 'textPrimary' }}
                />
              </ListItemButton>
            </ListItem>
          );
        })}
        {!isAuthenticated && (
          <>
            <Divider sx={{ my: 1 }} />
            <ListItem disablePadding>
              <ListItemButton component={Link} to="/login" onClick={handleDrawerToggle}><ListItemText primary="Sign in" /></ListItemButton>
            </ListItem>
            <ListItem disablePadding>
              <ListItemButton component={Link} to="/register" onClick={handleDrawerToggle}><ListItemText primary="Start free trial" primaryTypographyProps={{ fontWeight: 600, color: 'primary' }} /></ListItemButton>
            </ListItem>
          </>
        )}
      </List>
    </>
  );

  return (
    <StyledAppBar position="sticky">
      <Toolbar sx={{ justifyContent: 'space-between', minHeight: 56, gap: 1 }}>
        <Box
          component={Link}
          to={isAuthenticated ? '/dashboard' : '/'}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            textDecoration: 'none',
            color: 'primary.main',
            flexShrink: 0,
          }}
        >
          <Logo type="flahafast" size="small" />
          <Typography
            variant="subtitle1"
            sx={{ fontWeight: 700, letterSpacing: '-0.02em', display: { xs: 'none', sm: 'block' } }}
          >
            FlahaFAST
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
          {!isMobile && (
            <Box component="nav" aria-label="Main" sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
              {items.map((item) => {
                const active = isNavActive(location.pathname, item);
                return (
                  <Button
                    key={item.text}
                    {...linkProps(item)}
                    size="small"
                    aria-current={active ? 'page' : undefined}
                    sx={{
                      minWidth: 0,
                      px: 1.25,
                      color: active ? 'primary.main' : 'text.secondary',
                      fontWeight: active ? 600 : 400,
                      borderBottom: active ? 2 : 0,
                      borderColor: 'primary.main',
                      borderRadius: 0,
                    }}
                  >
                    {item.text}
                  </Button>
                );
              })}
            </Box>
          )}

          {!isAuthenticated && (
            <>
              <Button component={Link} to="/login" size="small" sx={{ display: { xs: 'none', sm: 'inline-flex' } }}>
                Sign in
              </Button>
              <Button component={Link} to="/register" size="small" variant="contained" sx={{ display: { xs: 'none', md: 'inline-flex' } }}>
                Start free trial
              </Button>
            </>
          )}

          {isAuthenticated && user ? (
            <>
              {subscription?.tier && subscription.tier !== 'FREE' && (
                <Chip
                  label={subscription.tier}
                  size="small"
                  variant="outlined"
                  sx={{ display: { xs: 'none', md: 'inline-flex' }, height: 24 }}
                />
              )}
              {!user.emailVerified && (
                <Chip
                  label="Verify email"
                  size="small"
                  color="warning"
                  variant="outlined"
                  sx={{ display: { xs: 'none', sm: 'inline-flex' }, height: 24 }}
                />
              )}
              <IconButton onClick={handleUserMenuOpen} sx={{ p: 0.5 }} aria-label={`${getInitials()}, account menu`}>
                <Avatar
                  sx={{
                    width: 32,
                    height: 32,
                    bgcolor: 'primary.main',
                    fontSize: '0.8rem',
                  }}
                >
                  {getInitials()}
                </Avatar>
              </IconButton>
              <Menu
                anchorEl={userMenuAnchor}
                open={Boolean(userMenuAnchor)}
                onClose={handleUserMenuClose}
                onClick={handleUserMenuClose}
                transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
              >
                <MenuItem component={Link} to="/profile" disabled>
                  <Box>
                    <Typography variant="body2" fontWeight={600}>
                      {user.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {user.email}
                    </Typography>
                  </Box>
                </MenuItem>
                <Divider />
                <MenuItem component={Link} to="/profile">
                  <Person fontSize="small" sx={{ mr: 1 }} />
                  Profile
                </MenuItem>
                <MenuItem onClick={handleLogout}>
                  <Logout fontSize="small" sx={{ mr: 1 }} />
                  Sign out
                </MenuItem>
              </Menu>
            </>
          ) : null}

          {isMobile && (
            <>
              <IconButton
                color="inherit"
                aria-label="Open menu"
                edge="end"
                onClick={handleDrawerToggle}
              >
                <MenuIcon />
              </IconButton>
              <Drawer
                anchor="right"
                variant="temporary"
                open={mobileOpen}
                onClose={handleDrawerToggle}
                ModalProps={{ keepMounted: true }}
                PaperProps={{ sx: { width: 260 } }}
              >
                {drawer}
              </Drawer>
            </>
          )}
        </Box>
      </Toolbar>
    </StyledAppBar>
  );
};

export default Header;
